import apiClient from '@/lib/api-client'
import type {
  AdminResource,
  ResourceBulkImportResponse,
  ResourceCategory,
  ResourceCreate,
  ResourceDownloadResponse,
  ResourceListResponse,
  ResourceUploadUrlRequest,
  ResourceUploadUrlResponse,
} from '@/types'

export const resourceService = {
  async listResources(category?: ResourceCategory): Promise<ResourceListResponse> {
    const response = await apiClient.get<ResourceListResponse>('/api/v1/resources', {
      params: { category },
    })
    return response.data
  },

  async getDownloadUrl(resourceId: number): Promise<ResourceDownloadResponse> {
    const response = await apiClient.get<ResourceDownloadResponse>(
      `/api/v1/resources/${resourceId}/download`,
    )
    return response.data
  },

  /** Download resource file to the user's device. */
  async triggerResourceDownload(resourceId: number, downloadFilename: string): Promise<void> {
    const result = await this.getDownloadUrl(resourceId)
    const response = await fetch(result.download_url)
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`)
    }
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const urlPath = new URL(result.download_url).pathname
    const ext = urlPath.includes('.') ? `.${urlPath.split('.').pop()}` : ''
    const filename = downloadFilename.includes('.') ? downloadFilename : `${downloadFilename}${ext}`
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  async adminListResources(): Promise<{ items: AdminResource[]; total: number }> {
    const response = await apiClient.get<{ items: AdminResource[]; total: number }>(
      '/api/v1/admin/resources',
    )
    return response.data
  },

  async adminCreateResource(data: ResourceCreate): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>(
      '/api/v1/admin/resources',
      data,
    )
    return response.data
  },

  async adminGetUploadUrl(
    resourceId: number,
    data: ResourceUploadUrlRequest,
  ): Promise<ResourceUploadUrlResponse> {
    const response = await apiClient.post<ResourceUploadUrlResponse>(
      `/api/v1/admin/resources/${resourceId}/upload-url`,
      data,
    )
    return response.data
  },

  async adminToggle(resourceId: number, isActive: boolean): Promise<void> {
    await apiClient.patch(`/api/v1/admin/resources/${resourceId}/toggle`, null, {
      params: { is_active: isActive },
    })
  },

  async adminDownloadResourceTemplate(): Promise<Blob> {
    const response = await apiClient.get('/api/v1/admin/resources/import/template', {
      responseType: 'blob',
    })
    return response.data as Blob
  },

  async adminImportResourcesCsv(file: File): Promise<ResourceBulkImportResponse> {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<ResourceBulkImportResponse>(
      '/api/v1/admin/resources/import',
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return response.data
  },

  async uploadToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    })
    if (!response.ok) {
      throw new Error(`S3 upload failed (${response.status})`)
    }
  },
}
